import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullSegment } from 'ts/shared/interfaces/MOResponse';
import { reasons1Short } from 'constants/mo';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { useLocation } from 'react-router-dom';
import withErrorBoundary from '../../withErrorBoundary';
import { addYears } from 'date-fns';
import MoSpecialBabyName from './MoSpecialBabyName';

interface SpecialRequest {
  code: string;
  data: any;
}

interface MoSpecialRequestShortProps {
  segment?: FullSegment;
  book?: any;
  paxes?: any;
  setSpecialRequestList: (message: SpecialRequest[]) => void;
  specialRequestList: SpecialRequest[];
  onSubmit: (message: string) => void;
}

const MoSpecialRequestShort: FC<MoSpecialRequestShortProps> = ({ segment, ...props }) => {
  const [t] = useTranslation();
  const [reasons1, setReasons1] = useState(reasons1Short);
  const location = useLocation();
  const firstCall = useRef(true);
  const [showBabyNameModal, setShowBabyNameModal] = useState(false);
  const openBaby = useRef('');
  const isNotRelativePath =
    location.pathname !== '/hotel/book' && location.pathname !== '/hotel2/book';

  useEffect(() => {
    if (isNotRelativePath) {
      return;
    }
    firstCall.current = true;
    setReasons1(reasons1Short);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (isNotRelativePath) {
      return;
    }

    if (props.book && props.paxes && firstCall.current) {
      const roomsPaxesI = props.book?.roomClass.hotelRooms.map((room: any) => room.paxCodes);
      const roomsPaxes = roomsPaxesI.map((paxI: any) =>
        paxI.map((paxI: any) => props.paxes?.passengers.find((pax: any) => pax.paxCode === paxI))
      );

      const isAnyChild = props.book?.paxes.some((pax: any) => pax.paxTypeCode === 'CNN');

      const isAnyInfant = props.book?.paxes.some(
        (pax: any) => pax.paxTypeCode === 'CNN' && addYears(new Date(pax.dob), 2) > new Date()
      );

      let newReasons1 = JSON.parse(JSON.stringify(reasons1Short));

      if (isAnyChild && roomsPaxes.length > 1) {
        newReasons1[2].visible = true;
        newReasons1[3].visible = true;
      }

      if (isAnyInfant) {
        newReasons1[4].visible = true;
      } else {
        newReasons1[5].visible = true;
      }

      setReasons1(newReasons1);

      firstCall.current = false;
      props.setSpecialRequestList(
        newReasons1
          .filter((reason: any) => reason.checked)
          .map((reason: any) => ({
            code: reason.key,
            data: reason.data || {},
          }))
      );
    } else {
      setReasons1((prev) =>
        prev.map((reason: any) => ({
          ...reason,
          checked: props.specialRequestList.some((req) => req.code === reason.key),
        }))
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, props.paxes]);

  const onClickCheckbox = (checked: boolean, reason: string) => {
    if ((reason === 'mo.confirm_baby_cot' || reason === 'mo.add_confirm_baby_cot') && checked) {
      setShowBabyNameModal(true);
      openBaby.current = reason;

      return;
    }

    if (checked) {
      const newSpecialRequestList = [...props.specialRequestList, { code: reason, data: {} }];
      props.setSpecialRequestList(newSpecialRequestList);
      onSubmit(newSpecialRequestList);
    } else {
      const newSpecialRequestList = props.specialRequestList.filter((r) => r.code !== reason);
      props.setSpecialRequestList(newSpecialRequestList);
      onSubmit(newSpecialRequestList);
    }
  };

  const onSubmitBaby = (model: any) => {
    setReasons1((prevReasons) => {
      const updatedReasons = prevReasons.map((reason) => {
        if (reason.key === openBaby.current) {
          return {
            ...reason,
            data: { fullName: model.fullName ? model.fullName : '____', dob: model.dob },
          };
        }
        return reason;
      });
      return updatedReasons;
    });

    const newRecord = {
      code: openBaby.current,
      data: { fullName: model.fullName ? model.fullName : '____', dob: model.dob },
    };
    const newSpecialRequestList = [...props.specialRequestList, newRecord];

    props.setSpecialRequestList(newSpecialRequestList);
    openBaby.current = '';

    onSubmit(newSpecialRequestList);
  };

  const onSubmit = (newSpecialRequestList: SpecialRequest[]) => {
    props.onSubmit(
      newSpecialRequestList
        .map((req) => {
          const translated = t(req.code, req.data) as string;
          return translated.replace(/<[^>]*>?/g, '');
        })
        .join('\n')
    );
  };

  return (
    <div>
      <div className="row checkbox-end">
        <div className=" col-12 text-lh mt-20 text-sm">
          {reasons1
            .slice(0, 4)
            .filter((reason) => reason.visible)
            .map((reason, index) => (
              <Checkbox
                className="mb-5"
                key={index}
                checked={reason.checked}
                onChange={(val) => onClickCheckbox(val, reason.key)}
                htmlLabel={<span dangerouslySetInnerHTML={{ __html: t(reason.key) }}></span>}
              />
            ))}
          {reasons1[4].visible && (
            <Fragment>
              <div className="mb-5 text-bold color-danger">{t('mo.if_child')}</div>
              {reasons1.slice(4, 5).map((reason, index) => (
                <Checkbox
                  className="mb-5"
                  key={index}
                  checked={reason.checked}
                  onChange={(val) => onClickCheckbox(val, reason.key)}
                  htmlLabel={<span dangerouslySetInnerHTML={{ __html: t(reason.key) }}></span>}
                />
              ))}
            </Fragment>
          )}

          {reasons1[5].visible && (
            <Fragment>
              <div className="mb-5 text-bold color-danger">{t('mo.if_no_child')}</div>
              {reasons1.slice(5, 6).map((reason, index) => (
                <Checkbox
                  className="mb-5"
                  key={index}
                  checked={reason.checked}
                  onChange={(val) => onClickCheckbox(val, reason.key)}
                  htmlLabel={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t(reason.key, { fullName: '____', dob: '____' }),
                      }}
                    ></span>
                  }
                />
              ))}
            </Fragment>
          )}
        </div>
      </div>
      {showBabyNameModal && (
        <MoSpecialBabyName onClose={() => setShowBabyNameModal(false)} onSubmit={onSubmitBaby} />
      )}
    </div>
  );
};

export default withErrorBoundary(MoSpecialRequestShort, 'MoSpecialRequestShort');
